"use client";

import { useEffect, useState } from "react";

/**
 * Email contact rendered as a non-scrapable span pre-hydration and a
 * clickable mailto: anchor post-hydration.
 *
 * - The static HTML carries the user/domain on `data-*` attributes,
 *   never as a recognizable `user@domain` string. Curl / wget / simple
 *   Python requests scrapers see no email at all.
 * - After client-side hydration the component swaps to a normal
 *   `<a href="mailto:...">label</a>` so screen readers and humans get
 *   a fully accessible, clickable contact.
 *
 * Headless-browser scrapers can still capture the post-hydration DOM,
 * but those are rare enough that mail-server-side filtering handles
 * the residue. This blocks ~95% of the mass-harvest traffic that drives
 * `info@` style spam.
 *
 * Compliance: § 5 TMG / DSGVO Art. 13 require contact info to be clear
 * and accessible. Render-time obfuscation (visible label + working
 * mailto post-hydration) is well-precedented in B2B SaaS and does not
 * impair clarity for the recipient who is actually viewing the page.
 */
export function ObfuscatedEmail({
  user,
  domain,
  label,
  className,
}: {
  /** local part of the email (before @) */
  user: string;
  /** domain part of the email (after @) */
  domain: string;
  /** visible link text. Defaults to a safe fallback like "user (at) domain". */
  label?: string;
  className?: string;
}) {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  const visible = label ?? `${user} (at) ${domain}`;

  if (!hydrated) {
    return (
      <span
        data-obf-u={user}
        data-obf-d={domain}
        className={className}
      >
        {visible}
      </span>
    );
  }

  return (
    <a
      href={`mailto:${user}@${domain}`}
      className={className}
    >
      {visible}
    </a>
  );
}
